import type { SyncStatus } from './swBridge';

export interface SyncStatusView {
  /** Short label next to the icons. */
  label: string;
  /** Longer line for the tooltip. */
  detail: string;
  /** Pending-work line for the tooltip, when any. */
  pendingDetail?: string;
  spinning: boolean;
  syncColorClass: string;
}

// TODO: centrally define semantic styles
const GOOD = 'text-green-600 dark:text-green-500';
const PENDING = 'text-amber-600 dark:text-amber-500';
const BAD = 'text-destructive';

/** Pure status → view mapping (kept out of the component for testing). */
export function describeSyncStatus(
  status: SyncStatus | undefined,
  online: boolean,
  formatRelative: (date: Date) => string,
): SyncStatusView {
  const pending = status ? status.pendingUploads + status.pendingEvents : 0;
  const pendingDetail =
    status && pending > 0
      ? `${status.pendingUploads} upload(s), ${status.pendingEvents} change(s) waiting`
      : undefined;
  const lastSynced = status?.lastSyncedAt
    ? `Last synced ${formatRelative(new Date(status.lastSyncedAt))}`
    : 'Not synced yet';

  if (!status) {
    return {
      label: online ? 'Online' : 'Offline',
      detail: lastSynced,
      spinning: false,
      syncColorClass: GOOD,
    };
  }

  switch (status.state) {
    case 'syncing':
      return {
        label: 'Syncing…',
        detail: status.stage ? `Syncing (${status.stage})` : 'Syncing',
        pendingDetail,
        spinning: true,
        syncColorClass: pending > 0 ? PENDING : GOOD,
      };
    case 'failed':
      return {
        label: 'Sync failed',
        detail: status.error
          ? `Last sync failed: ${status.error}`
          : 'Last sync failed',
        pendingDetail,
        spinning: false,
        syncColorClass: BAD,
      };
    case 'auth-required':
      return {
        label: 'Sign in required',
        detail: 'Sync is paused until you sign in again',
        pendingDetail,
        spinning: false,
        syncColorClass: BAD,
      };
    case 'aborted':
      // A previous run's SW died mid-sync; the watchdog re-triggers shortly.
      return {
        label: 'Sync interrupted',
        detail: 'Sync was interrupted — it resumes automatically',
        pendingDetail,
        spinning: false,
        syncColorClass: PENDING,
      };
    case 'offline':
      return {
        label: pending > 0 ? 'Offline — changes queued' : 'Offline',
        detail: lastSynced,
        pendingDetail,
        spinning: false,
        syncColorClass: pending > 0 ? PENDING : GOOD,
      };
    case 'succeeded':
    case 'idle':
      return {
        label: pending > 0 ? 'Changes queued' : 'Synced',
        detail: lastSynced,
        pendingDetail,
        spinning: false,
        syncColorClass: pending > 0 ? PENDING : GOOD,
      };
  }
}
